import { useCallback } from "react";
import {
  useAssessmentByBorrowerQuery,
  useCreateAssessmentMutation,
  useUpdateAssessmentMutation,
} from "./queries";
import { UnderwriterAssessmentFormData } from "@/types/underwriting";

interface UseUnderwritingAssessmentReturn {
  existingAssessment: import("../types/underwriting").UnderwritingAssessment | null;
  checkingAssessment: boolean;
  submitting: boolean;
  submitError: string | null;
  submit: (data: UnderwriterAssessmentFormData) => Promise<void>;
  update: (id: number, data: UnderwriterAssessmentFormData) => Promise<void>;
  refresh: () => void;
}

export const useUnderwritingAssessment = (
  borrowerId: number | undefined
): UseUnderwritingAssessmentReturn => {
  const id = borrowerId ?? null;
  const {
    data: existingAssessment,
    isLoading: checkingAssessment,
    refetch,
  } = useAssessmentByBorrowerQuery(id);
  const createMutation = useCreateAssessmentMutation();
  const updateMutation = useUpdateAssessmentMutation();

  const submitting = createMutation.isPending || updateMutation.isPending;
  const submitError =
    (createMutation.error as Error)?.message ?? (updateMutation.error as Error)?.message ?? null;

  const refresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const submit = useCallback(
    async (data: UnderwriterAssessmentFormData) => {
      try {
        await createMutation.mutateAsync(data);
        await refetch();
      } catch (err) {
        throw err;
      }
    },
    [createMutation, refetch]
  );

  const update = useCallback(
    async (idNum: number, data: UnderwriterAssessmentFormData) => {
      try {
        await updateMutation.mutateAsync({ id: idNum, data });
        await refetch();
      } catch (err) {
        throw err;
      }
    },
    [updateMutation, refetch]
  );

  return {
    existingAssessment: existingAssessment ?? null,
    checkingAssessment,
    submitting,
    submitError,
    submit,
    update,
    refresh,
  };
};
